import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useNotificationStore } from '@/stores/notification'

/**
 * Determina qual página de login usar baseado na rota de destino
 */
function getLoginRoute(to: RouteLocationNormalized) {
  // Se a rota é do módulo /usuario, usar a página de login específica
  if (to.path.startsWith('/usuario')) {
    return { name: 'UsuarioLogin', query: { redirect: to.fullPath } }
  }
  // Caso contrário, usar a página de login genérica
  return { name: 'Login', query: { redirect: to.fullPath } }
}

/**
 * Guard de Sessão
 * Verifica se a sessão do usuário ainda é válida
 * 
 * IMPORTANTE: Este guard só faz logout em casos de autenticação realmente inválida (401),
 * NÃO em casos de erro de rede ou timeout. Isso evita logout prematuro quando:
 * - A rede está lenta
 * - O servidor está temporariamente indisponível
 * - Há múltiplas navegações rápidas
 */
export const sessionGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()
  const notificationStore = useNotificationStore()

  try {
    const currentPath = typeof to.path === 'string' ? to.path : ''
    const isPasswordRecoveryRoute =
      currentPath === '/forgot-password' || currentPath.startsWith('/reset-password/')

    // Rotas guest (especialmente recuperação de senha) não devem ser bloqueadas por validação de sessão.
    if (to.meta?.guestOnly || isPasswordRecoveryRoute) {
      if (isPasswordRecoveryRoute) {
        console.log('[DeepLinkDebug][SessionGuard] bypass para rota de recuperação', {
          toPath: to.path,
          fromPath: from.path,
          isAuthenticated: authStore.isAuthenticated
        })
      }
      next()
      return
    }

    // Skip session validation if user just logged in (coming from login page)
    // This prevents spurious error messages when the session is valid but validation fails momentarily
    const isComingFromLogin = from.name === 'Login' || from.name === 'UsuarioLogin' || from.path === '/login' || from.path === '/usuario/login'
    if (isComingFromLogin) {
      // User just logged in, trust the authentication state
      next()
      return
    }

    // Verificar se a sessão está ativa
    if (authStore.isAuthenticated) {
      try {
        // Verificar se o token ainda é válido
        const validationResult = await authStore.validateSession()

        // validateSession agora retorna { valid: boolean, authError?: boolean }
        // Se retornar false (legacy) ou { valid: false, authError: true }, significa que a autenticação falhou
        const isValid = typeof validationResult === 'boolean' ? validationResult : validationResult?.valid !== false
        const isAuthError = typeof validationResult === 'object' && validationResult?.authError === true

        if (!isValid && isAuthError) {
          // Autenticação realmente inválida (401) - fazer logout
          console.warn('[sessionGuard] Autenticação inválida, redirecionando para login')
          authStore.logout()
          notificationStore.showError('Sessão expirada. Faça login novamente.')
          next(getLoginRoute(to))
          return
        }

        // Se não é um erro de autenticação (pode ser erro de rede), continuar navegando
        // O usuário ainda pode usar a aplicação com dados em cache
      } catch (error: any) {
        // Verificar se é um erro de autenticação (401) vs erro de rede/servidor
        const isAuthError = error?.response?.status === 401

        if (isAuthError) {
          console.warn('[sessionGuard] Erro 401 na validação da sessão, redirecionando para login')
          authStore.logout()
          notificationStore.showError('Sessão expirada. Faça login novamente.')
          next(getLoginRoute(to))
          return
        }

        // Para erros de rede, timeout, 5xx, etc., apenas logar e continuar
        // O usuário ainda pode usar a aplicação com o token local
        console.warn('[sessionGuard] Erro de rede na validação da sessão, continuando navegação:', error?.message)
      }
    }

    next()
  } catch (error) {
    console.error('Erro no guard de sessão:', error)
    // Em caso de erro inesperado, continuar navegação em vez de fazer logout
    // Isso é mais seguro para a experiência do usuário
    next()
  }
}

/**
 * Guard de Token Refresh
 * Tenta renovar o token automaticamente se necessário
 */
export const tokenRefreshGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext
) => {
  const authStore = useAuthStore()
  const notificationStore = useNotificationStore()

  try {
    if (authStore.isAuthenticated && authStore.token) {
      // Verificar se o token está próximo do vencimento
      // Aqui você pode implementar lógica para verificar a expiração do token
      // Por enquanto, vamos apenas validar a sessão
      const isValid = await authStore.validateSession()

      if (!isValid) {
        // Tentar renovar o token
        try {
          await authStore.refreshAuthToken()
        } catch (refreshError) {
          console.error('Erro ao renovar token:', refreshError)
          authStore.logout()
          notificationStore.showError('Sessão expirada. Faça login novamente.')
          next(getLoginRoute(to))
          return
        }
      }
    }

    next()
  } catch (error) {
    console.error('Erro no guard de refresh de token:', error)
    authStore.logout()
    notificationStore.showError('Erro na renovação do token.')
    next(getLoginRoute(to))
  }
}
